import { observable } from '@/libs/state'
import type { CompanyWorkflow, Member, Team, WorkItem } from './domain/workflow'
import type { CanvasEdge, CanvasNode } from './application/workflow-canvas-layout'

type NodePosition = { x: number; y: number }
type CanvasPositions = Record<string, NodePosition>

const CANVAS_POSITIONS_KEY = 'journ:canvas-positions'

const loadCanvasPositions = (): Record<string, CanvasPositions> => {
	try {
		const raw = localStorage.getItem(CANVAS_POSITIONS_KEY)
		return raw ? JSON.parse(raw) : {}
	} catch {
		return {}
	}
}

const saveCanvasPositions = (value: Record<string, CanvasPositions>) => {
	try {
		localStorage.setItem(CANVAS_POSITIONS_KEY, JSON.stringify(value))
	} catch {
		// storage quota exceeded - silently ignore
	}
}

const canvasPositions$ = observable<Record<string, CanvasPositions>>(loadCanvasPositions())

type PendingConnection = {
	fromStepId: string
	toStepId: string
}

type PendingTransitionConnect = {
	transitionId: string
	fromStepId: string
}

const workflows$ = observable<CompanyWorkflow[]>([])
const allWorkflows$ = observable<CompanyWorkflow[]>([])
const selectedWorkflowId$ = observable<string | null>(null)
const activeWorkflow$ = observable<CompanyWorkflow | null>(null)
const teams$ = observable<Team[]>([])
const workItems$ = observable<WorkItem[]>([])
const members$ = observable<Member[]>([])
const selectedTeamId$ = observable<string | null>(null)
const selectedPhaseId$ = observable<string | null>(null)
const selectedTransitionEdgeId$ = observable<string | null>(null)
const matrixQ$ = observable<string>('')
const matrixFilterStatus$ = observable<string[]>([])
const matrixFilterTeams$ = observable<string[]>([])
const matrixFilterPhases$ = observable<string[]>([])
const pendingConnection$ = observable<PendingConnection | null>(null)
const pendingTransitionConnect$ = observable<PendingTransitionConnect | null>(null)
const addingPhase$ = observable<boolean>(false)
const addingStep$ = observable<boolean>(false)
const editingPhaseId$ = observable<string | null>(null)
const editingStepId$ = observable<string | null>(null)
const canvasNodes$ = observable<CanvasNode[]>([])
const canvasEdges$ = observable<CanvasEdge[]>([])
const viewingWorkItemId$ = observable<string | null>(null)
const editingWorkItemId$ = observable<string | null>(null)
const creatingWorkItem$ = observable<boolean>(false)

// Local override for kanban column order per phase. Persisted in memory only;
// lost on full refresh but survives work-item updates and navigation.
const kanbanColumnOrder$ = observable<Record<string, string[]>>({})

// Shared board clock - a single now() tick driving every card's due/age display,
// so each card derives urgency from one source instead of calling Date.now().
// Seeded at creation so the first render is correct; the board re-ticks it.
const boardNowMs$ = observable<number>(Date.now())
export type { PendingConnection, PendingTransitionConnect, NodePosition, CanvasPositions }
export { loadCanvasPositions, saveCanvasPositions, canvasPositions$ }

export {
	workflows$,
	allWorkflows$,
	selectedWorkflowId$,
	activeWorkflow$,
	teams$,
	workItems$,
	members$,
	selectedTeamId$,
	selectedPhaseId$,
	selectedTransitionEdgeId$,
	matrixQ$,
	matrixFilterStatus$,
	matrixFilterTeams$,
	matrixFilterPhases$,
	pendingConnection$,
	pendingTransitionConnect$,
	addingPhase$,
	addingStep$,
	editingPhaseId$,
	editingStepId$,
	canvasNodes$,
	canvasEdges$,
	viewingWorkItemId$,
	editingWorkItemId$,
	creatingWorkItem$,
	kanbanColumnOrder$,
	boardNowMs$,
}
